import React, { useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import Application from "../../apiResources/Application";
import ActionBar from "../shared/general/ActionBar";
import FlashMessage from "../shared/general/FlashMessage";
import FullScreenLoading from "../shared/loaders/FullScreenLoading";
import ErrorBoundary from "../shared/error/ErrorBoundary";
import PersonalInfo from "./PersonalInfo";
import WorkInformation from "./WorkInformation";
import JobApplications from "./JobApplications";
import AdditionalInformation from "./AdditionalInformation";
import {
  Wrapper,
  Inner,
  EditButton,
  EditButtonLink,
} from "./styles/candidates-record-page-styles";
import Candidate from "../../apiResources/Candidate";
import CandidateEditModal from "../CandidateEditModal";
import { useModal, useFlash } from "../../hooks";

const CandidateRecordPage = () => {
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);

  const modal = useModal(false);
  const flash = useFlash();

  const url = window.location.href;
  const candidateId = url.substring(url.lastIndexOf("/") + 1).split("?")[0];

  const fetchCandidate = async () => {
    const applicationResponse = await Application.where({
      candidate_id: candidateId,
    })
      .where({ open: true, include_deleted: false })
      .includes({ engagement: "job" })
      .all();

    setApplications(applicationResponse.data);

    const response = await Candidate.selectExtra(["cv"])
      .includes([{ recruiter: "currency" }])
      .find(candidateId);

    setCandidate(response.data);
    setLoading(false);
  };

  useEffect(() => {
    void fetchCandidate();
  }, []);

  return (
    <ErrorBoundary>
      {!loading && (
        <>
          <EditButtonLink
            id="edit-candidate-record"
            href={`/recruiter/candidates/${candidate.id}/edit`}
            onClick={(event) => {
              event.preventDefault();
              modal.open();
            }}
            data-tip="Edit"
            data-for="candidate-record-tooltip"
          >
            <EditButton>
              <FontAwesomeIcon icon={faPencilAlt} />
            </EditButton>
          </EditButtonLink>
          <ActionBar
            title={`${candidate.fullName} - candidate`}
            mobileTitle={candidate.fullName}
            subtitle="Candidate"
            loading={loading}
            backLink="Candidates"
            backLinkUrl="/recruiter/candidates"
            mobileBackButton
          />
        </>
      )}
      {loading ? (
        <FullScreenLoading />
      ) : (
        <Wrapper>
          <ReactTooltip
            place="bottom"
            type="dark"
            effect="solid"
            aria-haspopup="true"
            id="candidate-record-tooltip"
          />
          <Inner>
            <PersonalInfo candidate={candidate} />
            <WorkInformation candidate={candidate} />
            <JobApplications
              candidate={candidate}
              applications={applications}
            />
            <AdditionalInformation candidate={candidate} />
          </Inner>
          <CandidateEditModal
            candidate={candidate.dup()}
            isOpen={modal.isOpen}
            onSuccess={(updatedCandidate) => {
              setCandidate(updatedCandidate);
              modal.close();
              flash.show();
            }}
            onDismiss={() => {
              modal.close();
            }}
          />
          <FlashMessage
            message="Successfully updated candidate"
            success
            showFlashMessage={flash.visible}
          />
        </Wrapper>
      )}
    </ErrorBoundary>
  );
};

export default CandidateRecordPage;
